/**
 * RIFT — armory.
 *
 * The gold-coin store: buy a weapon, then equip it into the primary or
 * secondary slot. Self-contained like `mapvote.js` — one class, its own
 * scoped `.ra-` stylesheet, no assets, same dark tactical-HUD palette.
 *
 *     const armory = new Armory(host, { onClose: () => {} });
 *     armory.show();
 *
 * Balance and ownership are the server's word (`../econ.js`, which the
 * server backs with `server/src/econ.rs` — see `HANDOFF.md` for why a price
 * or a balance only this bundle knew about would be a price a modified
 * client could lie about). This screen only shows them and asks the server
 * to spend.
 *
 * The chosen loadout is written to `localStorage['cod:loadout']` as a
 * `{ primary, secondary, melee, utility }` object and takes effect on the next
 * boot — the viewmodel is built once at engine init from those equipped items,
 * the same way a map vote only takes effect
 * on the reload that follows it (see `ARCHITECTURE.md`: "the world is
 * assembled once per boot").
 */
import {
  WEAPON_DEFS,
  LOADOUT_SLOTS,
  STARTER_LOADOUT,
  slotForWeapon,
} from '../weapons/defs/index.js';
import { getWallet, purchaseWeapon } from '../econ.js';
import { HUD_TOKENS, HUD_CSS, mountCorners } from './theme.js';

const STYLE_ID = 'ra-armory-style';

/**
 * Five stat bars per card, read straight off `WEAPON_DEFS` — the same tuning
 * data `WeaponSystem` fires with, not decorative numbers. Denominators are
 * fixed headroom picked from the data's own spread (a sniper's ~95 damage, an
 * SMG's ~950 rpm, a shotgun's ~450 max range) rather than the dataset's live
 * max, so one new weapon can't silently squash every existing bar.
 */
const STAT_LABELS = ['DMG', 'RANGE', 'RATE', 'ACCURACY', 'HANDLING'];

function clampPct(t) {
  return Math.round(Math.max(0.03, Math.min(1, t)) * 100);
}

function statPct(kind, w) {
  switch (kind) {
    case 'DMG':
      return clampPct(((w.pellets ? w.damage * w.pellets : w.damage) ?? 0) / 140);
    case 'RANGE':
      return clampPct((w.maxRange ?? 0) / 450);
    case 'RATE':
      return clampPct((w.rpm ?? 0) / 1000);
    case 'ACCURACY':
      return clampPct(1 - (w.spreadHip ?? 0) / 3.5);
    case 'HANDLING':
      return clampPct(1 - (w.adsTime ?? 0.3) / 0.4);
    default:
      return 0;
  }
}

export const LOADOUT_KEY = 'cod:loadout';
export const DEFAULT_LOADOUT = STARTER_LOADOUT;

function validSlotItem(slot, id) {
  if (slot === 'utility' && id === 'frag') return true;
  return !!WEAPON_DEFS[id] && slotForWeapon(id) === slot;
}

/** Parses+validates whatever is in localStorage; never trusts it blindly. */
export function readStoredLoadout() {
  try {
    const raw = JSON.parse(globalThis.localStorage?.getItem(LOADOUT_KEY) ?? 'null');
    // Migrate the old two-item format without throwing away a player's guns.
    if (Array.isArray(raw) && raw.length === 2 && raw.every((id) => WEAPON_DEFS[id])) {
      return { ...STARTER_LOADOUT, primary: raw[0], secondary: raw[1] };
    }
    if (raw && typeof raw === 'object') {
      const out = { ...STARTER_LOADOUT };
      for (const slot of LOADOUT_SLOTS) {
        if (validSlotItem(slot, raw[slot])) out[slot] = raw[slot];
      }
      return out;
    }
  } catch {
    /* fall through to the default */
  }
  return null;
}

export class Armory {
  constructor(host, opts = {}) {
    this.host = host || document.body;
    this.onClose = opts.onClose ?? null;

    this.balance = 0;
    this.owned = new Set();
    const stored = readStoredLoadout() ?? DEFAULT_LOADOUT;
    this.slots = { ...stored };
    this.visible = false;
    this._rows = new Map();

    this._injectStyle();
    this._build();
    this._refresh();
  }

  _el(tag, cls, parent, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    if (parent) parent.appendChild(n);
    return n;
  }

  _injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  _build() {
    this.root = this._el('section', 'ra-root', null);
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', 'Armory');
    this.root.hidden = true;
    mountCorners(this.root);

    const head = this._el('div', 'ra-head', this.root);
    const headL = this._el('div', null, head);
    this._el('div', 'hud-eyebrow', headL, 'ARMORY');
    this._el('h1', 'ra-title hud-title', headL, 'GOLD · WEAPONS');
    this._el(
      'p',
      'ra-copy',
      headL,
      'Earn coins in battles, buy new gear, then equip one item in each of your four weapon slots.'
    );
    this.balanceEl = this._el('div', 'ra-balance', head);
    this._el('span', 'ra-balance-lbl', this.balanceEl, 'BALANCE');
    this.balanceNum = this._el('span', 'ra-balance-num', this.balanceEl, '—');

    const slotsRow = this._el('div', 'ra-slots', this.root);
    this.slotEls = {};
    for (const slot of LOADOUT_SLOTS) {
      const box = this._el('div', 'ra-slot', slotsRow);
      this._el('span', 'ra-slot-lbl', box, slot.toUpperCase());
      const name = this._el('span', 'ra-slot-name', box, '');
      this.slotEls[slot] = name;
    }

    this.grid = this._el('div', 'ra-grid', this.root);
    const ids = Object.keys(WEAPON_DEFS).sort((a, b) => {
      const wa = WEAPON_DEFS[a], wb = WEAPON_DEFS[b];
      if ((wa.class === 'melee') !== (wb.class === 'melee')) return wa.class === 'melee' ? 1 : -1;
      return wa.price - wb.price || a.localeCompare(b);
    });
    for (const id of ids) this._card(id);

    const foot = this._el('div', 'ra-foot', this.root);
    this.flash = this._el('div', 'ra-flash', foot, '');
    const close = this._el('button', 'hud-btn hud-btn-primary ra-close', foot, 'CLOSE');
    close.type = 'button';
    close.addEventListener('click', () => this._close());

    this.host.appendChild(this.root);
  }

  _card(id) {
    const w = WEAPON_DEFS[id];
    const slot = slotForWeapon(w);
    const c = this._el('div', 'ra-card' + (slot === 'melee' ? ' ra-card-melee' : ''), this.grid);
    const top = this._el('div', 'ra-card-top', c);
    this._el('span', 'ra-name', top, w.label);
    this._el('span', 'ra-class', top, w.class.toUpperCase());
    if (w.caliber) this._el('div', 'ra-cal', c, w.caliber);

    const stats = this._el('div', 'ra-stats', c);
    for (const label of STAT_LABELS) {
      const row = this._el('div', 'ra-stat', stats);
      this._el('span', 'ra-stat-k', row, label);
      const bar = this._el('div', 'hud-bar ra-stat-bar', row);
      const fill = this._el('i', null, bar);
      fill.style.width = statPct(label, w) + '%';
    }

    this._el('span', `ra-price${w.price === 0 ? ' ra-starter' : ''}`, c, w.price === 0 ? 'STARTER' : `${w.price}`);

    const actions = this._el('div', 'ra-actions', c);
    const buy = this._el('button', 'hud-btn ra-buy', actions, 'BUY');
    buy.type = 'button';
    buy.addEventListener('click', () => this._buy(id));

    const equip = this._el('button', 'hud-btn ra-equip', actions, slot.toUpperCase());
    equip.type = 'button';
    equip.addEventListener('click', () => this._equip(slot, id));

    this._rows.set(id, { el: c, buy, equip, slot });
  }

  _owns(id) {
    const w = WEAPON_DEFS[id];
    return w.price === 0 || this.owned.has(id);
  }

  async _refresh() {
    try {
      const wallet = await getWallet();
      this.balance = wallet.balance ?? 0;
      this.owned = new Set(wallet.owned ?? []);
    } catch {
      this._say('OFFLINE — showing your last known balance.');
    }
    this.balanceNum.textContent = `${this.balance}g`;
    for (const [id, row] of this._rows) {
      const w = WEAPON_DEFS[id];
      const owned = this._owns(id);
      row.buy.hidden = owned;
      row.buy.disabled = this.balance < w.price;
      row.equip.hidden = !owned;
      row.el.classList.toggle('ra-owned', owned);
      row.equip.classList.toggle('ra-on', this.slots[row.slot] === id);
    }
    for (const slot of LOADOUT_SLOTS) {
      const id = this.slots[slot];
      this.slotEls[slot].textContent = id === 'frag' ? 'FRAG GRENADE' : WEAPON_DEFS[id]?.label ?? '—';
    }
  }

  async _buy(id) {
    const w = WEAPON_DEFS[id];
    const row = this._rows.get(id);
    row.buy.disabled = true;
    try {
      const result = await purchaseWeapon(id);
      this.balance = result.balance ?? this.balance;
      this.owned = new Set(result.owned ?? [...this.owned, id]);
      this._say(`BOUGHT ${w.label.toUpperCase()}`);
    } catch (e) {
      this._say(e.message || 'Purchase failed.');
    }
    this._refresh();
  }

  _equip(slot, id) {
    if (!this._owns(id)) return;
    if (slotForWeapon(id) !== slot) return;
    this.slots[slot] = id;
    this._persist();
    this._refresh();
  }

  _persist() {
    try {
      globalThis.localStorage?.setItem(LOADOUT_KEY, JSON.stringify(this.slots));
    } catch {
      /* localStorage unavailable (private mode, quota) — loadout just resets to default next boot */
    }
  }

  _say(msg) {
    this.flash.textContent = msg;
    this.flash.classList.remove('ra-flash-on');
    void this.flash.offsetWidth;
    this.flash.classList.add('ra-flash-on');
  }

  _close() {
    this._persist();
    this.hide();
    this.onClose?.();
  }

  show() {
    if (this.visible) return;
    this.visible = true;
    this.root.hidden = false;
    void this.root.offsetWidth;
    this.root.classList.add('ra-live');
    this._refresh();
  }

  hide() {
    if (!this.visible) return;
    this.visible = false;
    this.root.classList.remove('ra-live');
    this.root.hidden = true;
  }

  dispose() {
    this.hide();
    try {
      this.root?.remove();
    } catch {
      /* already gone */
    }
  }
}

const CSS = `
/* The display:flex below outranks the UA's [hidden]{display:none}, so without
   this closing the screen leaves a transparent z-index:60 sheet across the
   viewport that silently eats every click on the menu underneath. */
.ra-root[hidden]{display:none!important}
.ra-root {
  ${HUD_TOKENS}
  position:fixed; inset:0; z-index:60; display:flex; flex-direction:column;
  gap:clamp(10px,1.6vh,20px); padding:clamp(16px,3vh,44px) clamp(18px,4vw,64px);
  background:
    radial-gradient(60% 55% at 18% 14%, rgba(255,176,42,.22) 0%, transparent 60%),
    radial-gradient(55% 60% at 86% 88%, rgba(121,210,255,.18) 0%, transparent 65%),
    radial-gradient(140% 100% at 50% 50%, rgba(22,32,42,.92) 0%, rgba(4,6,9,.99) 70%),
    linear-gradient(180deg, #0a0e14 0%, #04060a 100%);
  color:var(--ink); font-family:var(--ff); text-transform:uppercase; letter-spacing:.05em;
  opacity:0; transition:opacity .22s ease; overflow:hidden;
}
.ra-root.ra-live { opacity:1; }
.ra-root > * { position:relative; z-index:1; }
${HUD_CSS}

.ra-head { display:flex; align-items:flex-start; justify-content:space-between; gap:28px; flex:0 0 auto;
  padding:24px 30px;
  background: linear-gradient(90deg, rgba(18,26,34,.96) 0%, rgba(10,16,21,.85) 60%, rgba(8,13,17,.55) 100%);
  border:1px solid rgba(190,214,230,.3);
  clip-path: var(--shear);
  position:relative;
  box-shadow: 0 14px 44px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.08); }
.ra-head::before { content:''; position:absolute; left:0; top:0; right:0; height:1px;
  background: linear-gradient(90deg, transparent 5%, var(--hair) 30%, var(--hair) 70%, transparent 95%); }
.ra-head::after { content:''; position:absolute; left:0; top:14px; bottom:14px; width:5px;
  background: linear-gradient(180deg, transparent, var(--amber) 25%, var(--amber-2) 75%, transparent);
  box-shadow: 0 0 20px rgba(255,176,42,.78), 0 0 40px rgba(255,176,42,.3); }
.ra-head-eyebrow { display:block; color:var(--amber); font-family:var(--fm); font-size:9.5px; letter-spacing:.34em;
  text-shadow: 0 0 12px rgba(255,176,42,.7), 0 0 24px rgba(255,176,42,.32); margin-bottom:8px; }
.ra-title { margin:.05em 0 .35em; font-size:clamp(32px,3.8vw,54px); line-height:1; }
.ra-copy { margin:6px 0 0; max-width:64ch; color:var(--ink-2); font-size:12.5px; letter-spacing:.04em;
  text-transform:none; line-height:1.6; }
.ra-headclose { margin-top:6px; }

.ra-balance { display:flex; flex-direction:column; align-items:flex-end; gap:6px; flex:0 0 auto;
  padding:16px 24px 16px 20px;
  background:
    radial-gradient(80% 100% at 100% 50%, rgba(255,176,42,.2) 0%, transparent 70%),
    linear-gradient(180deg, rgba(20,28,36,.94), rgba(8,13,17,.85));
  border:1px solid rgba(255,176,42,.6);
  clip-path: var(--shear-sm);
  box-shadow: 0 10px 28px rgba(255,176,42,.22), inset 0 1px 0 rgba(255,216,132,.28), 0 0 36px rgba(255,176,42,.14);
  position:relative; min-width:200px; }
.ra-balance::before { content:''; position:absolute; left:14px; top:50%; transform:translateY(-50%);
  width:20px; height:20px; border-radius:50%;
  background: radial-gradient(circle at 35% 35%, #ffe8a4 0%, var(--amber-2) 40%, var(--amber) 70%, #5e3608 100%);
  box-shadow: 0 0 16px rgba(255,176,42,.75), inset 0 1px 0 rgba(255,255,255,.45), inset 0 -2px 3px rgba(0,0,0,.35);
  border:1px solid rgba(255,224,156,.55); }
.ra-balance::after { content:'G'; position:absolute; left:14px; top:50%; transform:translate(-1px,-50%);
  width:20px; height:20px; display:flex; align-items:center; justify-content:center;
  font-family:var(--fd); font-size:11px; font-weight:800; color:#3a1f02;
  text-shadow: 0 1px 0 rgba(255,224,156,.6); z-index:1; }
.ra-balance-lbl { font-family:var(--fm); font-size:9px; letter-spacing:.28em; color:var(--ink-3);
  display:flex; align-items:center; gap:8px; padding-left:32px;
  text-transform:uppercase; }
.ra-balance-lbl::after { content:''; width:5px; height:5px; background:var(--amber); border-radius:50%;
  box-shadow:0 0 10px var(--amber), 0 0 18px rgba(255,176,42,.5); animation: ra-pulse 1.6s ease-in-out infinite; }
.ra-balance-num { font-family:var(--fd); font-size:36px; color:var(--amber-3); font-weight:800;
  text-shadow: 0 0 22px rgba(255,176,42,.78), 0 0 40px rgba(255,176,42,.32), 0 2px 6px rgba(0,0,0,.6);
  letter-spacing:.04em; padding-left:32px; line-height:1; }
@keyframes ra-pulse { 0%,100%{ transform:scale(1); opacity:1;} 50% { transform:scale(1.5); opacity:.5;} }

.ra-slots { display:flex; flex-wrap:wrap; gap:14px; flex:0 0 auto; }
.ra-slot { display:flex; flex-direction:column; gap:5px; padding:14px 22px; background:linear-gradient(180deg, rgba(16,24,32,.94), rgba(8,13,17,.85));
  border:1px solid rgba(190,214,230,.28); clip-path:var(--shear-sm); min-width:180px;
  box-shadow: 0 6px 20px rgba(0,0,0,.5), inset 0 1px 0 rgba(255,255,255,.06);
  transition: all .2s ease; position:relative; }
.ra-slot::before { content:''; position:absolute; left:0; top:12px; bottom:12px; width:3px;
  background: linear-gradient(180deg, transparent, var(--cyan), transparent);
  box-shadow: 0 0 10px var(--cyan); }
.ra-slot:hover { border-color: rgba(255,176,42,.6); transform: translateY(-2px);
  box-shadow: 0 10px 28px rgba(0,0,0,.55), 0 0 22px rgba(255,176,42,.18); }
.ra-slot-lbl { font-family:var(--fm); font-size:9px; letter-spacing:.24em; color:var(--ink-3);
  display:flex; align-items:center; gap:8px; padding-left:8px; }
.ra-slot-lbl::before { content:''; width:10px; height:1px; background: var(--cyan); box-shadow: 0 0 8px var(--cyan); }
.ra-slot-name { font-family:var(--fd); font-size:20px; font-weight:700; letter-spacing:.05em; color:var(--cyan-2);
  text-shadow: 0 0 16px rgba(121,210,255,.7), 0 0 32px rgba(121,210,255,.32), 0 2px 4px rgba(0,0,0,.6);
  padding-left:8px; }

.ra-grid { flex:1 1 auto; display:grid; gap:clamp(12px,1.3vw,20px); align-content:start;
  grid-template-columns:repeat(auto-fit,minmax(254px,1fr)); overflow:auto; padding:8px 2px 4px; }
.ra-card { display:flex; flex-direction:column; gap:10px; padding:18px 20px 16px; background:
  linear-gradient(180deg, rgba(22,30,40,.94) 0%, rgba(6,10,14,.96) 100%);
  border:1px solid rgba(190,214,230,.24); clip-path:var(--shear-sm);
  transition:border-color .2s ease, transform .2s ease, box-shadow .2s ease;
  position:relative; box-shadow: 0 10px 28px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.06); }
.ra-card::before { content:''; position:absolute; left:0; top:0; right:0; height:1px;
  background: linear-gradient(90deg, transparent 5%, var(--hair) 50%, transparent 95%); }
.ra-card::after { content:''; position:absolute; right:0; top:0; bottom:0; width:1px;
  background: linear-gradient(180deg, transparent, rgba(190,214,230,.16), transparent); }
.ra-card:hover { transform: translateY(-4px); border-color: rgba(255,176,42,.7);
  box-shadow: 0 18px 44px rgba(0,0,0,.65), 0 0 32px rgba(255,176,42,.18), inset 0 1px 0 rgba(255,255,255,.1); }
.ra-card.ra-owned { border-color:rgba(255,176,42,.78);
  box-shadow: 0 10px 28px rgba(0,0,0,.55), 0 0 28px rgba(255,176,42,.28), inset 0 1px 0 rgba(255,255,255,.06); }
.ra-card.ra-owned::before { background: linear-gradient(90deg, transparent, var(--amber), transparent); }
.ra-card.ra-owned::after { background: linear-gradient(180deg, transparent, var(--amber), transparent); }
.ra-card-melee { opacity:.92; }
.ra-card-top { display:flex; align-items:baseline; justify-content:space-between; gap:8px;
  padding-bottom:8px; border-bottom:1px solid rgba(190,214,230,.14); }
.ra-name { font-family:var(--fd); font-size:19px; font-weight:800; letter-spacing:.06em; color:#fff;
  text-shadow: 0 2px 10px rgba(0,0,0,.7), 0 0 18px rgba(255,255,255,.1); }
.ra-class { font-family:var(--fm); font-size:8.5px; letter-spacing:.2em; color:var(--amber);
  text-shadow: 0 0 10px rgba(255,176,42,.5); padding:2px 8px;
  background: linear-gradient(180deg, rgba(255,176,42,.12), rgba(255,176,42,.04));
  border:1px solid rgba(255,176,42,.35); }
.ra-cal { font-family:var(--fm); font-size:9px; letter-spacing:.1em; color:var(--ink-3); margin-top:-2px; }

.ra-stats { display:flex; flex-direction:column; gap:6px; margin:2px 0; padding:8px 0;
  border-top:1px solid rgba(190,214,230,.08); border-bottom:1px solid rgba(190,214,230,.08); }
.ra-stat { display:grid; grid-template-columns:64px 1fr; align-items:center; gap:10px; }
.ra-stat-k { font-family:var(--fm); font-size:8.5px; letter-spacing:.16em; color:var(--ink-3); }
.ra-stat-bar { height:5px; }
.ra-stat-bar > i { background:linear-gradient(90deg,#3d84a8,var(--cyan),var(--cyan-3));
  box-shadow:0 0 12px rgba(121,210,255,.65), inset 0 1px 0 rgba(255,255,255,.28); }
.ra-stat-bar > i::after { content:''; position:absolute; right:0; top:0; bottom:0; width:14px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.6)); }

.ra-price { font-family:var(--fd); font-weight:800; font-size:17px; color:var(--amber-3);
  text-shadow: 0 0 14px rgba(255,176,42,.6), 0 0 26px rgba(255,176,42,.25), 0 1px 2px rgba(0,0,0,.5);
  display:flex; align-items:baseline; gap:4px; }
.ra-price::after { content:'G'; font-size:11px; opacity:.7; margin-left:2px; }
.ra-price.ra-starter::after { content:''; }
.ra-actions { display:flex; gap:6px; margin-top:auto; padding-top:4px; }
.ra-buy, .ra-equip { flex:1 1 auto; padding:11px; font-size:10px; text-align:center;
  font-weight:800; transition: all .18s ease; }
.ra-buy { background:linear-gradient(180deg, var(--amber-3), var(--amber-2) 50%, var(--amber)); color:#100b02; border-color:rgba(255,224,156,.6);
  box-shadow: 0 5px 16px rgba(255,176,42,.42), inset 0 1px 0 rgba(255,255,255,.45), 0 0 26px rgba(255,176,42,.28);
  text-shadow: 0 1px 0 rgba(255,255,255,.3); }
.ra-buy:hover:not(:disabled) { background:linear-gradient(180deg, #ffe9a8, var(--amber-3) 50%, var(--amber-2)); color:#0a0600;
  box-shadow: 0 8px 24px rgba(255,176,42,.6), inset 0 1px 0 rgba(255,255,255,.55), 0 0 32px rgba(255,176,42,.4); transform: translateY(-1px); }
.ra-buy:disabled { background:rgba(190,214,230,.1); color:var(--ink-3); box-shadow: none; text-shadow:none; }
.ra-equip.ra-on { border-color:rgba(121,210,255,.9); color:#fff;
  background:linear-gradient(180deg, rgba(38,106,135,.55), rgba(38,106,135,.22));
  box-shadow: 0 0 22px rgba(121,210,255,.45), inset 0 1px 0 rgba(121,210,255,.4), inset 0 0 0 1px rgba(121,210,255,.25); }

.ra-foot { display:flex; align-items:center; gap:16px; flex:0 0 auto;
  padding:14px 22px; background:linear-gradient(90deg, rgba(12,18,24,.92), rgba(8,13,17,.7));
  border:1px solid rgba(190,214,230,.26);
  clip-path: var(--shear-sm);
  position:relative; }
.ra-foot::before { content:''; position:absolute; left:0; right:0; top:0; height:1px;
  background: linear-gradient(90deg, transparent, rgba(190,214,230,.4), transparent); }
.ra-flash { font-family:var(--fm); font-size:10px; letter-spacing:.18em; color:var(--amber-3); opacity:0; transition:opacity .2s ease;
  text-shadow: 0 0 12px rgba(255,176,42,.6); display:flex; align-items:center; gap:8px; }
.ra-flash::before { content:''; width:18px; height:1px; background:var(--amber); box-shadow: 0 0 8px var(--amber); }
.ra-flash-on { opacity:1; }
.ra-close { margin-left:auto; padding:12px 34px; font-weight:800; }
`;
